import { ReactElement, ReactNode } from 'react';
import { Box, Chip, useTheme } from '@mui/material';
import { emphasize } from '@mui/material/styles';
import { surfaceShadows } from '../../surfaceStyles';

/** A selected value is removable only when an onRemove handler is supplied. */
export function SelectionChip({
  label,
  icon,
  adornment,
  tone,
  onRemove,
}: {
  label: string;
  icon?: ReactElement;
  adornment?: ReactNode;
  tone?: { foreground: string; background: string };
  onRemove?: () => void;
}) {
  const theme = useTheme();
  const background = tone?.background || theme.palette.background.default;
  return (
    <Chip
      variant="filled"
      label={
        adornment ? (
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
          >
            {label}
            {adornment}
          </Box>
        ) : (
          label
        )
      }
      aria-label={onRemove ? `Remove ${label}` : undefined}
      icon={icon}
      onClick={onRemove}
      onDelete={onRemove}
      sx={{
        height: 40,
        borderRadius: 1,
        typography: 'body1',
        fontWeight: 500,
        maxWidth: '100%',
        '&.MuiChip-filled.MuiChip-colorDefault': {
          color: tone?.foreground || theme.palette.text.secondary,
          bgcolor: background,
          boxShadow: onRemove ? surfaceShadows.control : 'none',
          '&.MuiChip-clickable:hover, &.Mui-focusVisible': {
            bgcolor: emphasize(background, 0.06),
          },
          '&.MuiChip-clickable:active': {
            bgcolor: emphasize(background, 0.1),
            boxShadow: surfaceShadows.inset,
          },
        },
        '& .MuiChip-label': { px: 1.5 },
        '& .MuiChip-icon': { color: 'inherit' },
        '& .MuiChip-deleteIcon': {
          color: 'inherit',
          opacity: 0.65,
          '&:hover': { color: 'inherit', opacity: 1 },
        },
      }}
    />
  );
}
